import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Check, Heart, Shield, ArrowLeft, Coffee, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { createStripeCheckoutSession } from '../services/api';
import { useCohortDetails } from '../hooks/useCohortDetails';
import { SELF_PACED_INR } from '../utils/locale';
import checkoutImage from '../assets/images/checkout-image-1.png';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const productType = searchParams.get('productType') || 'self-paced'; // 'self-paced' or 'cohort'
  const [stripePromise, setStripePromise] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [includePartner, setIncludePartner] = useState(false);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { cohortDetails, loading: cohortLoading, isIndia } = useCohortDetails();

  const partnerId = process.env.REACT_APP_PARTNER_ID;
  const cohortId = process.env.REACT_APP_COHORT_ID;
  const indiaCohortId = process.env.REACT_APP_INDIA_COHORT_ID;
  const effectiveCohortId = isIndia && indiaCohortId ? indiaCohortId : cohortId;
  const currency = isIndia ? 'INR' : 'USD';
  const currencySymbol = isIndia ? '₹' : '$';

  // Partner discount config
  const partnerDiscountPercent = parseInt(process.env.REACT_APP_PARTNER_DISCOUNT_PERCENT || '50', 10);

  const cohortAmount = cohortDetails ? parseFloat(cohortDetails.cohort_fee) : (isIndia ? 49900 : 499.00);
  const cohortAmountFormatted = isIndia ? cohortAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : cohortAmount.toFixed(2);

  // Format cohort start date
  const formatCohortStartDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: userTimezone
    });
  };
  const cohortStartDate = cohortDetails?.start_date ? formatCohortStartDate(cohortDetails.start_date) : null;

  const productDetails = {
    'self-paced': {
      name: 'Contemplate Self-Paced Program - Mind Fitness for Parents',
      amount: isIndia ? SELF_PACED_INR : 199.00,
      currency,
      description: 'Learn at your own pace.',
      benefits: [
        'Immediate access to Week 1',
        '16 Contemplate principles',
        'Guided Contemplation audios',
        'Adioscope decision planner',
        'Speech control exercises',
        'Vision planner for all life areas',
        'One year access to the Contemplate app',
        'Cancel anytime'
      ],
      orderSummaryLabel: 'Contemplate Self-Paced Program - Mind Fitness for Parents',
      orderSummaryNote: ''
    },
    'cohort': {
      name: 'Contemplate Guided Cohort - 8-Week Program',
      amount: cohortAmount,
      currency,
      description: 'Learn with other parents like you. Progress fast with weekly accountability. Starts on ' + cohortStartDate + '. Runs for 8 weeks, once a week. Meeting link will be provided after registration.',
      benefits: [
        'Personalised feedback and guidance',
        'Learn with other parents who share your perspective',
        'Maintain accountability',
        'Coach led weekly training discipline for 5x better results',
        'One year access to the Contemplate app',
        'Join the Contemplate Parents community and attend relevant events throughout the year',
        'Discount on coach certification program if you apply and get selected (live session participants are strongly preferred for our coach certification program)'
      ],
      orderSummaryLabel: 'Guided Cohort Program',
      orderSummaryNote: cohortDetails?.start_date
        ? `One-time payment • Starts ${new Date(cohortDetails.start_date).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: userTimezone
        })}`
        : 'One-time payment • Starts 31 January 2026'
    }
  };

  const currentProduct = productDetails[productType];
  const baseAmount = currentProduct.amount;
  const partnerAmount = baseAmount * (1 - partnerDiscountPercent / 100);
  const totalAmount = includePartner ? baseAmount + partnerAmount : baseAmount;

  const formatAmount = (amt) => {
    if (productType === 'self-paced') {
      return isIndia ? amt.toLocaleString('en-IN') : amt.toFixed(2);
    }
    return isIndia ? amt.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : amt.toFixed(2);
  };

  const currentAmountFormatted = formatAmount(baseAmount);
  const partnerAmountFormatted = formatAmount(partnerAmount);
  const totalAmountFormatted = formatAmount(totalAmount);

  // Load Stripe
  useEffect(() => {
    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (stripePublishableKey) {
      const stripe = loadStripe(stripePublishableKey);
      setStripePromise(stripe);
      setIsLoading(false);
    } else {
      console.error('Stripe publishable key not configured');
      setIsLoading(false);
    }
  }, []);

  // Track checkout page view when product changes
  useEffect(() => {
    const currencyCode = currentProduct.currency?.toUpperCase() || (isIndia ? 'INR' : 'USD');
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: currencyCode,
        value: currentProduct.amount,
        items: [{ item_name: currentProduct.name, price: currentProduct.amount }]
      });
    }
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', { value: currentProduct.amount, currency: currencyCode });
    }
  }, [currentProduct.amount, currentProduct.name, currentProduct.currency, isIndia]);

  // Handle product type change
  const handleProductTypeChange = (newType) => {
    setSearchParams({ productType: newType });
  };

  // Handle Stripe checkout
  const handleStripeCheckout = async () => {
    setIsProcessing(true);
    try {
      const currency = currentProduct.currency || (isIndia ? 'inr' : 'usd');
      // Stripe expects amount in smallest unit: cents (USD) or paise (INR)
      const amountInSmallestUnit = Math.round(Number(totalAmount) * 100);

      const checkoutMetadata = {
        productType: productType,
        amount: totalAmount,
        currency,
        amountInSmallestUnit, // Use this + currency when creating Stripe session (unit_amount / total)
        productName: currentProduct.name,
        productDescription: currentProduct.description,
        successUrl: `${window.location.origin}/complete-registration?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/checkout?productType=${productType}`,
        timezone: userTimezone,
        includePartner: includePartner,
        ...(includePartner && {
          partnerDiscountPercent: partnerDiscountPercent,
          partnerAmount: partnerAmount,
          baseAmount: baseAmount,
        })
      };

      // Add cohort-specific metadata if it's a cohort product
      if (productType === 'cohort' && cohortDetails) {
        checkoutMetadata.cohortId = cohortDetails.id;
        checkoutMetadata.partnerId = cohortDetails.partner?.id;
        checkoutMetadata.programId = cohortDetails.program?.id;
      } else if (productType === 'cohort' && partnerId && effectiveCohortId) {
        checkoutMetadata.cohortId = effectiveCohortId;
        checkoutMetadata.partnerId = partnerId;
      }

      console.log('checkoutMetadata', checkoutMetadata);
      // Create checkout session on backend
      const sessionData = await createStripeCheckoutSession(checkoutMetadata);
      console.log('sessionData', sessionData);
      if (!sessionData.sessionId && !sessionData.url) {
        throw new Error('Failed to create checkout session');
      }

      // Redirect to Stripe Checkout using the session URL
      // The backend should return the checkout session URL directly
      if (sessionData.url) {
        // Direct redirect to Stripe Checkout URL
        window.location.href = sessionData.url;
      } else if (sessionData.sessionId) {
        // Fallback: Construct URL from session ID (if backend doesn't return URL)
        // This is the standard Stripe Checkout URL format
        window.location.href = `https://checkout.stripe.com/c/pay/${sessionData.sessionId}`;
      } else {
        throw new Error('No checkout URL or session ID provided');
      }
      // Note: User will be redirected away from this page, so we don't need to set isProcessing to false
    } catch (error) {
      console.error('Checkout error:', error);
      setIsProcessing(false);
      alert('Error starting checkout. Please try again.');
    }
  };


  const reinforcements = [
    { icon: Heart, text: 'You\'re choosing peace over conflict' },
    { icon: Coffee, text: 'Transform your coffee time into growth time' },
    { icon: Shield, text: 'Invest in a relationship that lasts forever' }
  ];

  return (
    <div className="min-h-screen bg-[#efede6]">
      {/* Header */}
      <div className="bg-[#efede6] border-b border-[#202020]/10">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#202020]/60 hover:text-[#202020] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Order Summary & Reinforcement */}
          <div>
            <h1 className="text-3xl md:text-4xl font-normal text-[#202020] mb-4 tracking-tight">
              You're making a <em className="font-serif italic">beautiful</em> choice
            </h1>
            <p className="text-[#202020]/60 text-lg mb-6">
              A choice for peace. For connection. For a relationship that will last a lifetime.
            </p>

            {/* Product Type Switcher */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#202020]/70 mb-3">
                Choose your program:
              </label>
              <div className="flex gap-3 bg-white rounded-xl p-1 border border-[#202020]/10">
                <button
                  onClick={() => handleProductTypeChange('self-paced')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${productType === 'self-paced'
                    ? 'bg-[#202020] text-[#efede6] shadow-md'
                    : 'text-[#202020]/60 hover:text-[#202020] hover:bg-[#202020]/5'
                    }`}
                >
                  Self-Paced
                </button>
                <button
                  onClick={() => handleProductTypeChange('cohort')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${productType === 'cohort'
                    ? 'bg-[#202020] text-[#efede6] shadow-md'
                    : 'text-[#202020]/60 hover:text-[#202020] hover:bg-[#202020]/5'
                    }`}
                >
                  Guided Cohort
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-2xl p-8 mb-6">
              <h3 className="text-lg font-medium text-[#202020] mb-6">Order Summary</h3>

              <div className="space-y-3 mb-6">
                {currentProduct.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#202020]/10 flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#202020]/10 pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-[#202020]/60">{currentProduct.orderSummaryLabel}</span>
                  <span className="text-2xl font-light text-[#202020]">{currencySymbol}{currentAmountFormatted}</span>
                </div>
                <p className="text-[#202020]/40 text-sm mt-2">{currentProduct.description}</p>
                <p className="text-[#202020]/40 text-sm mt-2">{currentProduct.orderSummaryNote}</p>

                {/* Partner line item */}
                {includePartner && (
                  <div className="mt-4 pt-4 border-t border-[#202020]/10">
                    <div className="flex justify-between items-center">
                      <span className="text-[#202020]/60">Partner's Program <span className="text-xs bg-[#202020] text-[#efede6] px-2 py-0.5 rounded-full ml-1">{partnerDiscountPercent}% off</span></span>
                      <span className="text-xl font-light text-[#202020]">{currencySymbol}{partnerAmountFormatted}</span>
                    </div>
                    <p className="text-[#202020]/40 text-sm mt-1">Same program for your partner at a discounted rate</p>
                  </div>
                )}

                {/* Total when partner is included */}
                {includePartner && (
                  <div className="mt-4 pt-4 border-t border-[#202020]/20">
                    <div className="flex justify-between items-center">
                      <span className="text-[#202020] font-medium">Total</span>
                      <span className="text-2xl font-medium text-[#202020]">{currencySymbol}{totalAmountFormatted}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Add Your Partner Toggle */}
            <div
              onClick={() => setIncludePartner(!includePartner)}
              className={`rounded-2xl p-6 mb-12 cursor-pointer transition-all duration-300 border-2 ${includePartner
                  ? 'bg-[#202020] border-[#202020]'
                  : 'bg-white border-[#202020]/10 hover:border-[#202020]/30'
                }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${includePartner ? 'bg-[#efede6]/20' : 'bg-[#202020]/5'
                  }`}>
                  <Users className={`w-5 h-5 transition-colors duration-300 ${includePartner ? 'text-[#efede6]' : 'text-[#202020]/60'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className={`font-medium transition-colors duration-300 ${includePartner ? 'text-[#efede6]' : 'text-[#202020]'}`}>
                      Add your partner
                    </h4>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors duration-300 ${includePartner ? 'bg-[#efede6]/20 text-[#efede6]' : 'bg-[#202020]/10 text-[#202020]'
                      }`}>
                      {partnerDiscountPercent}% off partner's fee
                    </span>
                  </div>
                  <p className={`text-sm transition-colors duration-300 ${includePartner ? 'text-[#efede6]/70' : 'text-[#202020]/50'}`}>
                    Partner's fee: <span className="font-medium">{currencySymbol}{partnerAmountFormatted}</span> <span className="line-through opacity-50">{currencySymbol}{currentAmountFormatted}</span>
                  </p>
                  <p className={`text-xs mt-2 italic transition-colors duration-300 ${includePartner ? 'text-[#efede6]/50' : 'text-[#202020]/40'}`}>
                    Recommended: Couples who do this together see 2× better results
                  </p>
                </div>
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all duration-300 ${includePartner ? 'bg-[#efede6] border-[#efede6]' : 'border-[#202020]/30'
                  }`}>
                  {includePartner && <Check className="w-4 h-4 text-[#202020]" />}
                </div>
              </div>
            </div>

            {/* Positive Reinforcement */}
            <div className="space-y-4">
              {reinforcements.map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white rounded-xl p-5">
                  <div className="w-12 h-12 rounded-full bg-[#202020]/5 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#202020]/70" />
                  </div>
                  <p className="text-[#202020] font-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Payment */}
          <div>
            {/* Checkout Image */}
            <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
              <img
                src={checkoutImage}
                alt="Contemplate - A peaceful choice for your family"
                className="w-full h-auto"
              />
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-xl font-medium text-[#202020] mb-2">Complete Your Purchase</h2>
              <p className="text-[#202020]/50 mb-8">Secure payment powered by Stripe</p>

              {isProcessing || isLoading || (productType === 'cohort' && cohortLoading) ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-2 border-[#202020]/20 border-t-[#202020] rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#202020]/60">
                    {isLoading ? 'Loading payment options...' :
                      cohortLoading ? 'Loading cohort details...' :
                        'Processing your payment...'}
                  </p>
                </div>
              ) : (
                <Button
                  onClick={handleStripeCheckout}
                  className="w-full bg-[#202020] text-[#efede6] hover:bg-[#202020]/90 py-6 text-lg font-medium rounded-xl transition-all"
                  disabled={isProcessing}
                >
                  Continue to Payment
                </Button>
              )}

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 mt-8 text-[#202020]/40">
                <Shield className="w-4 h-4" />
                <span className="text-xs">Secure, encrypted payment</span>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 text-center">
              <p className="text-[#202020]/40 text-sm mb-4">Trusted by 500+ peaceful parents</p>
              <div className="flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-[#202020]/60" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
